import type { Locale } from "./site";

const englishPrivacyPolicy = `
**Last updated:** June 2, 2026

This Privacy Policy explains how **inext** ("inext", "we", "us", or "our") collects, uses, stores, and protects personal data when you visit our website **inext.ltd**, contact us, request information, or communicate with us in relation to our products and services.

inext develops and supplies Android AOSP set-top boxes, media players, and related solutions for TV operators, OTT providers, ISPs, and other business customers.

This Privacy Policy applies only to our website and business communications. It does not apply to third-party websites, services, applications, or customer-operated platforms that may use inext products.

## 1. Who we are

The website **inext.ltd** is operated by:

**Company:** INEXT EUROPE s.r.o.  
**Registered address:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Email:** info@inext.ltd  
**Website:** https://inext.ltd

For privacy-related questions, you may contact us at: **info@inext.ltd**.

## 2. What personal data we collect

We may collect limited personal data when you interact with our website or communicate with us.

This may include:

### Contact and business information

Your name, company name, job title, business email address, phone number, country, and message content.

### Inquiry information

Product interests, project requirements, requested specifications, quantities, region, target market, certification needs, and other information you choose to provide.

### Technical website data

IP address, browser type, device type, operating system, pages visited, referring website, approximate location, date and time of visit, and similar analytics data.

### Communication data

Emails, attachments, meeting notes, and other correspondence exchanged between you and inext.

We do not intentionally collect sensitive personal data through our website.

## 3. How we collect personal data

We collect personal data when you:

- submit a contact form;
- request a quotation, product information, technical documentation, or a meeting;
- communicate with us by email, phone, messengers, or other business channels;
- visit or browse our website;
- interact with our marketing or business development materials.

Some technical data may be collected automatically through cookies, analytics tools, server logs, or similar technologies.

## 4. How we use personal data

We use personal data for legitimate business purposes, including to:

- respond to inquiries and business requests;
- provide information about our products, solutions, and capabilities;
- prepare quotations, proposals, and commercial offers;
- evaluate potential cooperation with TV, OTT, ISP, distribution, and technology partners;
- manage customer, supplier, and partner relationships;
- arrange calls, meetings, demonstrations, or product discussions;
- improve our website, communications, and services;
- protect the security and integrity of our website and business systems;
- comply with legal, tax, accounting, export control, sanctions, and regulatory obligations;
- establish, exercise, or defend legal claims.

We do not sell personal data.

## 5. Legal basis for processing

Where applicable data protection law requires a legal basis, we process personal data based on one or more of the following grounds:

### Legitimate interests

For business communications, website security, B2B marketing, responding to inquiries, and managing commercial relationships.

### Pre-contractual or contractual necessity

When processing is required to prepare or perform a contract, quotation, order, supply arrangement, NDA, or other business agreement.

### Legal obligation

When we must process or retain data to comply with applicable laws, accounting rules, tax rules, customs requirements, sanctions screening, or other legal obligations.

### Consent

Where we ask for your consent, for example for certain cookies, newsletters, or optional marketing communications.

You may withdraw consent at any time where processing is based on consent.

## 6. B2B marketing communications

We may contact business representatives with relevant information about our products, services, trade activities, or business opportunities, where permitted by applicable law.

You may opt out of marketing communications at any time by contacting us or using the unsubscribe option where available.

Even if you opt out of marketing communications, we may still contact you regarding existing business relationships, contracts, orders, technical matters, legal notices, or other non-marketing communications.

## 7. Cookies and analytics

Our website may use cookies and similar technologies to operate properly, improve performance, understand visitor behaviour, and support security.

Cookies may be used for:

- essential website functionality;
- analytics and performance measurement;
- remembering preferences;
- improving website content and usability.

Where required by law, we will request consent before placing non-essential cookies.

You can usually control or disable cookies through your browser settings. Disabling some cookies may affect website functionality.

## 8. Sharing personal data

We may share personal data only where reasonably necessary for business, operational, legal, or security purposes.

This may include sharing data with:

- hosting providers and IT service providers;
- email, CRM, analytics, and communication service providers;
- logistics, certification, technical, and manufacturing partners where required for a project;
- legal, accounting, tax, audit, and compliance advisers;
- public authorities, regulators, courts, or law enforcement where legally required;
- potential buyers, investors, or successors in connection with a business transaction, merger, restructuring, or sale of assets.

We require service providers to handle personal data securely and only for authorised purposes.

## 9. International transfers

As a B2B manufacturer working with international customers, partners, suppliers, and service providers, we may process or transfer personal data outside your country of residence.

Where required by applicable law, we use appropriate safeguards for international data transfers, such as contractual protections or other lawful transfer mechanisms.

## 10. Data retention

We keep personal data only for as long as reasonably necessary for the purposes described in this Privacy Policy.

Retention periods may depend on:

- the nature of the business relationship;
- whether we are discussing, preparing, or performing a contract;
- legal, tax, accounting, warranty, compliance, or regulatory requirements;
- the need to resolve disputes or protect our legal rights.

Business correspondence, quotations, and contract-related records may be retained for longer where necessary for legal, accounting, or commercial purposes.

## 11. Data security

We use reasonable technical and organisational measures to protect personal data against unauthorised access, loss, misuse, alteration, or disclosure.

However, no website, email system, or online transmission method is completely secure. You should avoid sending highly confidential information through unsecured channels unless appropriate protections are agreed in advance.

## 12. Your rights

Depending on your location and applicable law, you may have rights to:

- access your personal data;
- correct inaccurate or incomplete data;
- request deletion of your data;
- restrict or object to processing;
- withdraw consent where processing is based on consent;
- request data portability;
- lodge a complaint with a data protection authority.

To exercise your rights, contact us at **info@inext.ltd**.

We may need to verify your identity before responding to a request. Some requests may be limited by legal obligations, contractual requirements, legitimate business interests, or the rights of others.

## 13. Third-party websites and services

Our website may contain links to third-party websites, platforms, documentation, or partner resources.

We are not responsible for the privacy practices, content, or security of third-party websites or services. You should review their privacy policies before providing personal data to them.

## 14. Product and customer platform data

inext manufactures and supplies hardware and related solutions for business customers. In many cases, our customers operate their own platforms, services, applications, middleware, subscriber databases, analytics systems, billing systems, or content services.

Unless expressly agreed otherwise in a separate written agreement, inext does not control how our customers collect, use, or process end-user data through their own TV, OTT, ISP, or other services.

Customers are responsible for ensuring that their own services, applications, firmware configurations, and data processing activities comply with applicable privacy, telecommunications, consumer protection, and data protection laws.

## 15. Children's privacy

Our website and services are intended for business users and are not directed to children.

We do not knowingly collect personal data from children through our website. If you believe that a child has provided us with personal data, please contact us and we will take appropriate steps to delete it.

## 16. Changes to this Privacy Policy

We may update this Privacy Policy from time to time to reflect changes in our website, business operations, legal requirements, or data processing practices.

The updated version will be posted on this page with a new "Last updated" date.

## 17. Contact us

For questions about this Privacy Policy or our handling of personal data, please contact:

**INEXT EUROPE s.r.o.**  
**Email:** info@inext.ltd  
**Address:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Website:** https://inext.ltd
`;

const ukrainianPrivacyPolicy = `
**Останнє оновлення:** 2 червня 2026 року

Ця Політика конфіденційності пояснює, як **inext** ("inext", "ми", "нас" або "наша компанія") збирає, використовує, зберігає та захищає персональні дані, коли ви відвідуєте наш сайт **inext.ltd**, звертаєтеся до нас, запитуєте інформацію або комунікуєте з нами щодо наших продуктів і сервісів.

inext розробляє та постачає Android AOSP set-top box-приставки, медіаплеєри та пов’язані рішення для ТВ-операторів, OTT-провайдерів, ISP та інших бізнес-клієнтів.

Ця Політика конфіденційності застосовується лише до нашого сайту та бізнес-комунікацій. Вона не поширюється на сторонні сайти, сервіси, застосунки або платформи клієнтів, які можуть використовувати продукти inext.

## 1. Хто ми

Сайтом **inext.ltd** керує:

**Компанія:** INEXT EUROPE s.r.o.  
**Зареєстрована адреса:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Email:** info@inext.ltd  
**Вебсайт:** https://inext.ltd

З питань конфіденційності ви можете звертатися на: **info@inext.ltd**.

## 2. Які персональні дані ми збираємо

Ми можемо збирати обмежений обсяг персональних даних, коли ви взаємодієте з нашим сайтом або комунікуєте з нами.

Це може включати:

### Контактну та бізнес-інформацію

Ваше ім’я, назву компанії, посаду, робочу email-адресу, номер телефону, країну та зміст повідомлення.

### Інформацію про запит

Інтерес до продуктів, вимоги проєкту, запитані характеристики, кількість пристроїв, регіон, цільовий ринок, потреби в сертифікації та іншу інформацію, яку ви надаєте добровільно.

### Технічні дані сайту

IP-адресу, тип браузера, тип пристрою, операційну систему, відвідані сторінки, сайт-джерело переходу, приблизне місцезнаходження, дату й час відвідування та подібні аналітичні дані.

### Дані комунікацій

Листи, вкладення, нотатки зустрічей та іншу кореспонденцію між вами та inext.

Ми свідомо не збираємо чутливі персональні дані через наш сайт.

## 3. Як ми збираємо персональні дані

Ми збираємо персональні дані, коли ви:

- надсилаєте контактну форму;
- запитуєте комерційну пропозицію, інформацію про продукт, технічну документацію або зустріч;
- комунікуєте з нами email, телефоном, у месенджерах або через інші бізнес-канали;
- відвідуєте або переглядаєте наш сайт;
- взаємодієте з нашими маркетинговими або бізнес-матеріалами.

Деякі технічні дані можуть збиратися автоматично через cookies, аналітичні інструменти, серверні журнали або подібні технології.

## 4. Як ми використовуємо персональні дані

Ми використовуємо персональні дані для законних бізнес-цілей, зокрема щоб:

- відповідати на запити та бізнес-звернення;
- надавати інформацію про наші продукти, рішення та можливості;
- готувати кошториси, пропозиції та комерційні оферти;
- оцінювати потенційну співпрацю з ТВ, OTT, ISP, дистрибуційними та технологічними партнерами;
- керувати відносинами з клієнтами, постачальниками та партнерами;
- організовувати дзвінки, зустрічі, демонстрації або продуктові обговорення;
- покращувати сайт, комунікації та сервіси;
- захищати безпеку й цілісність сайту та бізнес-систем;
- виконувати юридичні, податкові, бухгалтерські, експортні, санкційні та регуляторні обов’язки;
- встановлювати, здійснювати або захищати юридичні вимоги.

Ми не продаємо персональні дані.

## 5. Правові підстави обробки

Коли застосовне законодавство про захист даних вимагає правову підставу, ми обробляємо персональні дані на одній або кількох з таких підстав:

### Законні інтереси

Для бізнес-комунікацій, безпеки сайту, B2B-маркетингу, відповіді на запити та керування комерційними відносинами.

### Переддоговірна або договірна необхідність

Коли обробка потрібна для підготовки або виконання договору, комерційної пропозиції, замовлення, постачання, NDA або іншої бізнес-угоди.

### Юридичний обов’язок

Коли ми повинні обробляти або зберігати дані для дотримання законів, бухгалтерських правил, податкових вимог, митних процедур, санкційних перевірок або інших юридичних обов’язків.

### Згода

Коли ми просимо вашу згоду, наприклад для окремих cookies, розсилок або необов’язкових маркетингових комунікацій.

Ви можете відкликати згоду в будь-який час, якщо обробка базується на згоді.

## 6. B2B-маркетингові комунікації

Ми можемо звертатися до представників бізнесу з релевантною інформацією про наші продукти, сервіси, виставкову активність або бізнес-можливості, якщо це дозволено застосовним законодавством.

Ви можете відмовитися від маркетингових комунікацій у будь-який час, звернувшись до нас або скориставшись опцією відписки, якщо вона доступна.

Навіть після відмови від маркетингових комунікацій ми можемо зв’язуватися з вами щодо чинних бізнес-відносин, договорів, замовлень, технічних питань, юридичних повідомлень або інших немаркетингових комунікацій.

## 7. Cookies та аналітика

Наш сайт може використовувати cookies і подібні технології для коректної роботи, покращення продуктивності, розуміння поведінки відвідувачів і підтримки безпеки.

Cookies можуть використовуватися для:

- базової функціональності сайту;
- аналітики та вимірювання продуктивності;
- запам’ятовування налаштувань;
- покращення контенту та зручності сайту.

Коли цього вимагає закон, ми запитуватимемо згоду перед розміщенням необов’язкових cookies.

Зазвичай ви можете керувати cookies або вимикати їх у налаштуваннях браузера. Вимкнення деяких cookies може вплинути на функціональність сайту.

## 8. Передача персональних даних

Ми можемо передавати персональні дані лише тоді, коли це обґрунтовано необхідно для бізнесових, операційних, юридичних або безпекових цілей.

Це може включати передачу даних:

- хостинг-провайдерам та ІТ-постачальникам;
- email, CRM, аналітичним і комунікаційним сервісам;
- логістичним, сертифікаційним, технічним і виробничим партнерам, якщо це потрібно для проєкту;
- юридичним, бухгалтерським, податковим, аудиторським і compliance-радникам;
- державним органам, регуляторам, судам або правоохоронним органам, коли це вимагається законом;
- потенційним покупцям, інвесторам або правонаступникам у зв’язку з бізнес-угодою, злиттям, реструктуризацією або продажем активів.

Ми вимагаємо від постачальників послуг безпечно обробляти персональні дані та використовувати їх лише для дозволених цілей.

## 9. Міжнародні передачі

Як B2B-виробник, що працює з міжнародними клієнтами, партнерами, постачальниками та сервісними провайдерами, ми можемо обробляти або передавати персональні дані за межі вашої країни проживання.

Коли цього вимагає застосовне законодавство, ми використовуємо належні гарантії для міжнародної передачі даних, зокрема договірний захист або інші законні механізми передачі.

## 10. Строк зберігання даних

Ми зберігаємо персональні дані лише стільки, скільки обґрунтовано необхідно для цілей, описаних у цій Політиці конфіденційності.

Строки зберігання можуть залежати від:

- характеру бізнес-відносин;
- того, чи ми обговорюємо, готуємо або виконуємо договір;
- юридичних, податкових, бухгалтерських, гарантійних, compliance або регуляторних вимог;
- потреби врегулювати спори або захистити наші юридичні права.

Бізнес-кореспонденція, комерційні пропозиції та договірні записи можуть зберігатися довше, якщо це потрібно для юридичних, бухгалтерських або комерційних цілей.

## 11. Безпека даних

Ми застосовуємо розумні технічні та організаційні заходи для захисту персональних даних від несанкціонованого доступу, втрати, неправомірного використання, зміни або розкриття.

Водночас жоден сайт, email-система або спосіб онлайн-передачі не є повністю безпечним. Не надсилайте висококонфіденційну інформацію незахищеними каналами, якщо відповідні заходи захисту не погоджені заздалегідь.

## 12. Ваші права

Залежно від вашого місцезнаходження та застосовного законодавства ви можете мати право:

- отримати доступ до ваших персональних даних;
- виправити неточні або неповні дані;
- вимагати видалення ваших даних;
- обмежити обробку або заперечити проти неї;
- відкликати згоду, якщо обробка базується на згоді;
- вимагати перенесення даних;
- подати скаргу до органу із захисту даних.

Щоб реалізувати свої права, зверніться до нас на **info@inext.ltd**.

Перед відповіддю на запит нам може знадобитися підтвердити вашу особу. Деякі запити можуть бути обмежені юридичними обов’язками, договірними вимогами, законними бізнес-інтересами або правами інших осіб.

## 13. Сторонні сайти та сервіси

Наш сайт може містити посилання на сторонні сайти, платформи, документацію або партнерські ресурси.

Ми не відповідаємо за практики конфіденційності, контент або безпеку сторонніх сайтів чи сервісів. Перед наданням персональних даних таким ресурсам ознайомтеся з їхніми політиками конфіденційності.

## 14. Дані продуктів і клієнтських платформ

inext виробляє та постачає обладнання й пов’язані рішення для бізнес-клієнтів. У багатьох випадках наші клієнти самостійно керують власними платформами, сервісами, застосунками, middleware, базами абонентів, аналітичними системами, білінгом або контентними сервісами.

Якщо інше прямо не погоджено в окремій письмовій угоді, inext не контролює, як наші клієнти збирають, використовують або обробляють дані кінцевих користувачів у власних ТВ, OTT, ISP або інших сервісах.

Клієнти відповідають за те, щоб їхні сервіси, застосунки, конфігурації прошивок і дії з обробки даних відповідали застосовним законам про конфіденційність, телекомунікації, захист споживачів і захист даних.

## 15. Конфіденційність дітей

Наш сайт і сервіси призначені для бізнес-користувачів і не орієнтовані на дітей.

Ми свідомо не збираємо персональні дані дітей через наш сайт. Якщо ви вважаєте, що дитина надала нам персональні дані, зверніться до нас, і ми вживемо належних заходів для їх видалення.

## 16. Зміни до цієї Політики конфіденційності

Ми можемо час від часу оновлювати цю Політику конфіденційності, щоб відобразити зміни на сайті, у бізнес-операціях, юридичних вимогах або практиках обробки даних.

Оновлена версія буде опублікована на цій сторінці з новою датою "Останнє оновлення".

## 17. Зв’язатися з нами

З питань щодо цієї Політики конфіденційності або нашої обробки персональних даних звертайтеся:

**INEXT EUROPE s.r.o.**  
**Email:** info@inext.ltd  
**Адреса:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Вебсайт:** https://inext.ltd
`;

const polishPrivacyPolicy = `
**Ostatnia aktualizacja:** 2 czerwca 2026 r.

Niniejsza Polityka prywatności wyjaśnia, w jaki sposób **inext** ("inext", "my", "nas" lub "nasza firma") zbiera, wykorzystuje, przechowuje i chroni dane osobowe, gdy odwiedzasz stronę **inext.ltd**, kontaktujesz się z nami lub prosisz o informacje o naszych produktach i usługach.

inext projektuje i dostarcza dekodery z systemem Android AOSP, odtwarzacze multimedialne oraz powiązane rozwiązania dla operatorów telewizyjnych, dostawców OTT i internetu oraz innych klientów biznesowych.

Niniejsza Polityka prywatności dotyczy wyłącznie naszej strony internetowej i komunikacji biznesowej. Nie obejmuje stron, usług, aplikacji ani platform klientów lub podmiotów trzecich, które mogą korzystać z produktów inext.

## 1. Kim jesteśmy

Stroną **inext.ltd** zarządza:

**Firma:** INEXT EUROPE s.r.o.  
**Adres rejestrowy:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**E-mail:** info@inext.ltd\
**Strona:** https://inext.ltd

W sprawach związanych z prywatnością możesz kontaktować się z nami pod adresem: **info@inext.ltd**.

## 2. Jakie dane osobowe zbieramy

Możemy zbierać ograniczony zakres danych osobowych, gdy korzystasz z naszej strony lub komunikujesz się z nami.

Może to obejmować:

### Dane kontaktowe i biznesowe

Imię i nazwisko, nazwę firmy, stanowisko, służbowy adres e-mail, numer telefonu, kraj oraz treść wiadomości.

### Informacje o zapytaniu

Zainteresowanie produktami, wymagania projektu, oczekiwane specyfikacje, ilości, region, rynek docelowy, potrzeby certyfikacyjne oraz inne informacje, które dobrowolnie nam przekazujesz.

### Dane techniczne strony

Adres IP, typ przeglądarki, typ urządzenia, system operacyjny, odwiedzone strony, strona odsyłająca, przybliżona lokalizacja, data i godzina wizyty oraz podobne dane analityczne.

### Dane komunikacyjne

Wiadomości e-mail, załączniki, notatki ze spotkań oraz inną korespondencję wymienianą między Tobą a inext.

Nie zbieramy celowo wrażliwych danych osobowych za pośrednictwem naszej strony.

## 3. Jak zbieramy dane osobowe

Zbieramy dane osobowe, gdy:

- wysyłasz formularz kontaktowy;
- prosisz o ofertę, informacje produktowe, dokumentację techniczną lub spotkanie;
- komunikujesz się z nami pocztą elektroniczną, telefonicznie, przez komunikatory lub inne kanały biznesowe;
- odwiedzasz lub przeglądasz naszą stronę;
- wchodzisz w interakcję z naszymi materiałami marketingowymi lub biznesowymi.

Część danych technicznych może być zbierana automatycznie przez pliki cookie, narzędzia analityczne, logi serwera lub podobne technologie.

## 4. Jak wykorzystujemy dane osobowe

Wykorzystujemy dane osobowe do uzasadnionych celów biznesowych, w tym aby:

- odpowiadać na zapytania i prośby biznesowe;
- przekazywać informacje o naszych produktach, rozwiązaniach i możliwościach;
- przygotowywać wyceny, propozycje i oferty handlowe;
- oceniać potencjalną współpracę z partnerami TV, OTT, ISP, dystrybucyjnymi i technologicznymi;
- zarządzać relacjami z klientami, dostawcami i partnerami;
- organizować rozmowy, spotkania, prezentacje lub dyskusje produktowe;
- ulepszać stronę, komunikację i usługi;
- chronić bezpieczeństwo i integralność naszej strony oraz systemów biznesowych;
- spełniać obowiązki prawne, podatkowe, księgowe, eksportowe, sankcyjne i regulacyjne;
- ustalać, dochodzić lub bronić roszczeń prawnych.

Nie sprzedajemy danych osobowych.

## 5. Podstawa prawna przetwarzania

Jeżeli właściwe przepisy o ochronie danych wymagają podstawy prawnej, przetwarzamy dane osobowe na podstawie jednej lub kilku z poniższych przesłanek:

### Uzasadniony interes

Dla komunikacji biznesowej, bezpieczeństwa strony, marketingu B2B, odpowiadania na zapytania i zarządzania relacjami handlowymi.

### Działania przedumowne lub wykonanie umowy

Gdy przetwarzanie jest potrzebne do przygotowania lub wykonania umowy, wyceny, zamówienia, dostawy, NDA lub innego porozumienia biznesowego.

### Obowiązek prawny

Gdy musimy przetwarzać lub przechowywać dane, aby spełnić wymogi prawa, księgowości, podatków, odpraw celnych, weryfikacji sankcyjnej lub innych obowiązków prawnych.

### Zgoda

Gdy prosimy o zgodę, na przykład dla wybranych plików cookie, newsletterów lub opcjonalnej komunikacji marketingowej.

Zgodę można wycofać w dowolnym momencie, jeżeli przetwarzanie odbywa się na jej podstawie.

## 6. Komunikacja marketingowa B2B

Możemy kontaktować się z przedstawicielami firm z istotnymi informacjami o naszych produktach, usługach, aktywnościach targowych lub możliwościach biznesowych, jeżeli pozwala na to obowiązujące prawo.

Możesz zrezygnować z komunikacji marketingowej w dowolnym momencie, kontaktując się z nami lub korzystając z opcji rezygnacji, jeśli jest dostępna.

Nawet po rezygnacji z komunikacji marketingowej możemy nadal kontaktować się z Tobą w sprawie istniejących relacji biznesowych, umów, zamówień, kwestii technicznych, informacji prawnych lub innych komunikatów niemarketingowych.

## 7. Pliki cookie i analityka

Nasza strona może używać plików cookie i podobnych technologii, aby działać prawidłowo, poprawiać wydajność, rozumieć zachowanie odwiedzających i wspierać bezpieczeństwo.

Pliki cookie mogą być używane do:

- niezbędnego działania strony;
- analityki i pomiaru wydajności;
- zapamiętywania preferencji;
- poprawy treści i użyteczności strony.

Gdy wymagają tego przepisy, poprosimy o zgodę przed umieszczeniem niekoniecznych plików cookie.

Zwykle możesz kontrolować lub wyłączyć pliki cookie w ustawieniach przeglądarki. Wyłączenie części plików cookie może wpłynąć na działanie strony.

## 8. Udostępnianie danych osobowych

Możemy udostępniać dane osobowe wyłącznie wtedy, gdy jest to rozsądnie potrzebne do celów biznesowych, operacyjnych, prawnych lub bezpieczeństwa.

Może to obejmować udostępnianie danych:

- dostawcom hostingu i usług IT;
- dostawcom poczty elektronicznej, systemów CRM, analityki i komunikacji;
- partnerom logistycznym, certyfikacyjnym, technicznym i produkcyjnym, gdy wymaga tego projekt;
- doradcom prawnym, księgowym, podatkowym i audytowym oraz specjalistom ds. zgodności;
- organom publicznym, regulatorom, sądom lub organom ścigania, gdy wymaga tego prawo;
- potencjalnym nabywcom, inwestorom lub następcom w związku z transakcją biznesową, połączeniem, restrukturyzacją lub sprzedażą aktywów.

Wymagamy od dostawców usług bezpiecznego przetwarzania danych osobowych i wykorzystywania ich wyłącznie do autoryzowanych celów.

## 9. Transfery międzynarodowe

Jako producent B2B współpracujący z międzynarodowymi klientami, partnerami, dostawcami i usługodawcami możemy przetwarzać lub przekazywać dane osobowe poza kraj Twojego zamieszkania.

Gdy wymaga tego obowiązujące prawo, stosujemy odpowiednie zabezpieczenia dla międzynarodowych transferów danych, takie jak zabezpieczenia umowne lub inne zgodne z prawem mechanizmy transferu.

## 10. Okres przechowywania danych

Przechowujemy dane osobowe tylko tak długo, jak jest to rozsądnie konieczne dla celów opisanych w niniejszej Polityce prywatności.

Okresy przechowywania mogą zależeć od:

- charakteru relacji biznesowej;
- tego, czy omawiamy, przygotowujemy lub wykonujemy umowę;
- wymagań prawnych, podatkowych, księgowych, gwarancyjnych lub regulacyjnych oraz wymogów zgodności;
- potrzeby rozstrzygania sporów lub ochrony naszych praw.

Korespondencja biznesowa, wyceny i dokumentacja związana z umowami mogą być przechowywane dłużej, jeżeli jest to potrzebne do celów prawnych, księgowych lub handlowych.

## 11. Bezpieczeństwo danych

Stosujemy rozsądne środki techniczne i organizacyjne, aby chronić dane osobowe przed nieuprawnionym dostępem, utratą, nadużyciem, zmianą lub ujawnieniem.

Żadna strona internetowa, system poczty elektronicznej ani metoda transmisji online nie są jednak całkowicie bezpieczne. Nie przesyłaj wysoce poufnych informacji niezabezpieczonymi kanałami, chyba że wcześniej uzgodniono odpowiednie środki ochrony.

## 12. Twoje prawa

W zależności od lokalizacji i obowiązującego prawa możesz mieć prawo do:

- dostępu do swoich danych osobowych;
- sprostowania niedokładnych lub niepełnych danych;
- żądania usunięcia danych;
- ograniczenia przetwarzania lub sprzeciwu wobec przetwarzania;
- wycofania zgody, gdy przetwarzanie opiera się na zgodzie;
- żądania przeniesienia danych;
- złożenia skargi do organu ochrony danych.

Aby skorzystać z praw, skontaktuj się z nami pod adresem **info@inext.ltd**.

Przed udzieleniem odpowiedzi możemy poprosić o potwierdzenie Twojej tożsamości. Zakres realizacji niektórych żądań może być ograniczony ze względu na obowiązki prawne, wymogi umowne, uzasadnione interesy biznesowe lub prawa innych osób.

## 13. Strony i usługi podmiotów trzecich

Nasza strona może zawierać linki do stron, platform, dokumentacji lub zasobów partnerskich podmiotów trzecich.

Nie odpowiadamy za praktyki prywatności, treść ani bezpieczeństwo stron lub usług podmiotów trzecich. Przed przekazaniem im danych osobowych zapoznaj się z ich politykami prywatności.

## 14. Dane produktów i platform klientów

inext produkuje i dostarcza sprzęt oraz powiązane rozwiązania dla klientów biznesowych. W wielu przypadkach nasi klienci samodzielnie prowadzą własne platformy, usługi, aplikacje, systemy pośredniczące, bazy abonentów oraz systemy analityczne, rozliczeniowe i treściowe.

O ile odrębna pisemna umowa nie stanowi inaczej, inext nie kontroluje sposobu, w jaki nasi klienci zbierają, wykorzystują lub przetwarzają dane użytkowników końcowych w swoich usługach TV, OTT, ISP lub innych.

Klienci odpowiadają za to, aby ich usługi, aplikacje, konfiguracje firmware i działania związane z przetwarzaniem danych były zgodne z właściwymi przepisami o prywatności, telekomunikacji, ochronie konsumentów i ochronie danych.

## 15. Prywatność dzieci

Nasza strona i usługi są przeznaczone dla użytkowników biznesowych i nie są kierowane do dzieci.

Nie zbieramy świadomie danych osobowych dzieci za pośrednictwem naszej strony. Jeśli uważasz, że dziecko przekazało nam dane osobowe, skontaktuj się z nami, a podejmiemy odpowiednie kroki w celu ich usunięcia.

## 16. Zmiany w niniejszej Polityce prywatności

Możemy okresowo aktualizować niniejszą Politykę prywatności, aby odzwierciedlała zmiany na stronie, w działalności biznesowej, wymaganiach prawnych lub praktykach przetwarzania danych.

Zaktualizowana wersja zostanie opublikowana na tej stronie z nową datą "Ostatnia aktualizacja".

## 17. Kontakt

W sprawach dotyczących niniejszej Polityki prywatności lub naszego przetwarzania danych osobowych skontaktuj się z:

**INEXT EUROPE s.r.o.**  
**E-mail:** info@inext.ltd\
**Adres:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Strona:** https://inext.ltd
`;

const brazilianPrivacyPolicy = `
**Última atualização:** 2 de junho de 2026

Esta Política de Privacidade explica como a **inext** ("inext", "nós" ou "nossa") coleta, usa, armazena e protege dados pessoais quando você visita nosso site **inext.ltd**, entra em contato conosco, solicita informações ou se comunica conosco em relação aos nossos produtos e serviços.

A inext desenvolve e fornece set-top boxes Android AOSP, media players e soluções relacionadas para operadoras de TV, provedores OTT, ISPs e outros clientes corporativos.

Esta Política de Privacidade aplica-se apenas ao nosso site e às comunicações corporativas. Ela não se aplica a sites, serviços, aplicativos ou plataformas operadas por nossos clientes que possam utilizar produtos inext.

## 1. Quem somos

O site **inext.ltd** é operado por:

**Empresa:** INEXT EUROPE s.r.o.  
**Endereço registrado:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Email:** info@inext.ltd  
**Site:** https://inext.ltd

Para questões relacionadas à privacidade, você pode entrar em contato conosco pelo: **info@inext.ltd**.

## 2. Quais dados pessoais coletamos

Podemos coletar dados pessoais limitados quando você interage com nosso site ou se comunica conosco.

Isso pode incluir:

### Informações de contato e corporativas

Seu nome, nome da empresa, cargo, endereço de e-mail corporativo, número de telefone, país e conteúdo da mensagem.

### Informações da solicitação

Interesse em produtos, requisitos do projeto, especificações solicitadas, quantidades, região, mercado-alvo, necessidades de certificação e outras informações que você optar por fornecer.

### Dados técnicos do site

Endereço IP, tipo de navegador, tipo de dispositivo, sistema operacional, páginas visitadas, site de referência, localização aproximada, data e hora da visita e dados analíticos semelhantes.

### Dados de comunicação

E-mails, anexos, notas de reuniões e demais correspondências trocadas entre você e a inext.

Não coletamos intencionalmente dados pessoais sensíveis por meio do nosso site.

## 3. Como coletamos dados pessoais

Coletamos dados pessoais quando você:

- envia um formulário de contato;
- solicita uma cotação, informações sobre produtos, documentação técnica ou uma reunião;
- comunica-se conosco por e-mail, telefone, aplicativos de mensagens ou outros canais corporativos;
- visita ou navega em nosso site;
- interage com nossos materiais de marketing ou de desenvolvimento de negócios.

Alguns dados técnicos podem ser coletados automaticamente por meio de cookies, ferramentas de análise, registros de servidor ou tecnologias semelhantes.

## 4. Como usamos os dados pessoais

Usamos os dados pessoais para finalidades comerciais legítimas, incluindo para:

- responder a consultas e solicitações comerciais;
- fornecer informações sobre nossos produtos, soluções e capacidades;
- preparar cotações, propostas e ofertas comerciais;
- avaliar potencial cooperação com parceiros de TV, OTT, ISP, distribuição e tecnologia;
- gerenciar relacionamentos com clientes, fornecedores e parceiros;
- agendar chamadas, reuniões, demonstrações ou conversas sobre produtos;
- aprimorar nosso site, nossas comunicações e nossos serviços;
- proteger a segurança e a integridade do nosso site e dos nossos sistemas corporativos;
- cumprir obrigações legais, fiscais, contábeis, de controle de exportação, de sanções e regulatórias;
- estabelecer, exercer ou defender direitos em processos judiciais.

Não vendemos dados pessoais.

## 5. Base legal para o tratamento

Quando a legislação de proteção de dados aplicável exige uma base legal, tratamos os dados pessoais com fundamento em uma ou mais das seguintes bases:

### Interesses legítimos

Para comunicações corporativas, segurança do site, marketing B2B, resposta a consultas e gerenciamento de relacionamentos comerciais.

### Necessidade pré-contratual ou contratual

Quando o tratamento é necessário para preparar ou executar um contrato, cotação, pedido, acordo de fornecimento, NDA ou outro acordo comercial.

### Obrigação legal

Quando precisamos tratar ou reter dados para cumprir leis aplicáveis, regras contábeis, regras fiscais, exigências aduaneiras, triagem de sanções ou outras obrigações legais.

### Consentimento

Quando solicitamos o seu consentimento, por exemplo, para determinados cookies, newsletters ou comunicações de marketing opcionais.

Você pode retirar o consentimento a qualquer momento quando o tratamento se basear no consentimento.

## 6. Comunicações de marketing B2B

Podemos entrar em contato com representantes corporativos para fornecer informações relevantes sobre nossos produtos, serviços, atividades comerciais ou oportunidades de negócios, quando permitido pela legislação aplicável.

Você pode cancelar o recebimento de comunicações de marketing a qualquer momento, entrando em contato conosco ou utilizando a opção de cancelamento de inscrição, quando disponível.

Mesmo que você cancele o recebimento de comunicações de marketing, ainda poderemos entrar em contato com você sobre relacionamentos comerciais existentes, contratos, pedidos, questões técnicas, notificações legais ou outras comunicações não relacionadas a marketing.

## 7. Cookies e análise

Nosso site pode usar cookies e tecnologias semelhantes para funcionar adequadamente, melhorar o desempenho, compreender o comportamento dos visitantes e dar suporte à segurança.

Os cookies podem ser usados para:

- funcionalidades essenciais do site;
- análise e medição de desempenho;
- memorização de preferências;
- aprimoramento do conteúdo e da usabilidade do site.

Quando exigido por lei, solicitaremos consentimento antes de instalar cookies não essenciais.

Geralmente, você pode controlar ou desativar os cookies nas configurações do seu navegador. A desativação de alguns cookies pode afetar a funcionalidade do site.

## 8. Compartilhamento de dados pessoais

Podemos compartilhar dados pessoais apenas quando razoavelmente necessário para finalidades comerciais, operacionais, legais ou de segurança.

Isso pode incluir o compartilhamento de dados com:

- provedores de hospedagem e provedores de serviços de TI;
- provedores de serviços de e-mail, CRM, análise e comunicação;
- parceiros de logística, certificação, técnicos e de fabricação, quando necessário para um projeto;
- consultores jurídicos, contábeis, fiscais, de auditoria e de compliance;
- autoridades públicas, órgãos reguladores, tribunais ou autoridades policiais, quando exigido por lei;
- potenciais compradores, investidores ou sucessores em conexão com uma transação comercial, fusão, reestruturação ou venda de ativos.

Exigimos que os provedores de serviços tratem os dados pessoais de forma segura e apenas para finalidades autorizadas.

## 9. Transferências internacionais

Como fabricante B2B que trabalha com clientes, parceiros, fornecedores e provedores de serviços internacionais, podemos tratar ou transferir dados pessoais para fora do seu país de residência.

Quando exigido pela legislação aplicável, utilizamos salvaguardas adequadas para transferências internacionais de dados, como proteções contratuais ou outros mecanismos legais de transferência.

## 10. Retenção de dados

Mantemos os dados pessoais apenas pelo tempo razoavelmente necessário para as finalidades descritas nesta Política de Privacidade.

Os períodos de retenção podem depender de:

- natureza do relacionamento comercial;
- se estamos discutindo, preparando ou executando um contrato;
- exigências legais, fiscais, contábeis, de garantia, de compliance ou regulatórias;
- necessidade de resolver disputas ou proteger nossos direitos legais.

Correspondências comerciais, cotações e registros relacionados a contratos podem ser retidos por mais tempo, quando necessário para finalidades legais, contábeis ou comerciais.

## 11. Segurança dos dados

Utilizamos medidas técnicas e organizacionais razoáveis para proteger os dados pessoais contra acesso não autorizado, perda, uso indevido, alteração ou divulgação.

No entanto, nenhum site, sistema de e-mail ou método de transmissão online é totalmente seguro. Você deve evitar enviar informações altamente confidenciais por canais não seguros, a menos que proteções adequadas sejam acordadas previamente.

## 12. Seus direitos

Dependendo da sua localização e da legislação aplicável, você pode ter o direito de:

- acessar seus dados pessoais;
- corrigir dados imprecisos ou incompletos;
- solicitar a exclusão dos seus dados;
- restringir ou se opor ao tratamento;
- retirar o consentimento quando o tratamento se basear no consentimento;
- solicitar a portabilidade dos dados;
- apresentar uma reclamação a uma autoridade de proteção de dados.

Para exercer seus direitos, entre em contato conosco pelo **info@inext.ltd**.

Podemos precisar verificar sua identidade antes de responder a uma solicitação. Algumas solicitações podem ser limitadas por obrigações legais, requisitos contratuais, interesses comerciais legítimos ou direitos de terceiros.

## 13. Sites e serviços de terceiros

Nosso site pode conter links para sites, plataformas, documentações ou recursos de parceiros de terceiros.

Não somos responsáveis pelas práticas de privacidade, pelo conteúdo ou pela segurança de sites ou serviços de terceiros. Você deve revisar as políticas de privacidade deles antes de fornecer dados pessoais.

## 14. Dados de produtos e plataformas de clientes

A inext fabrica e fornece hardware e soluções relacionadas para clientes corporativos. Em muitos casos, nossos clientes operam suas próprias plataformas, serviços, aplicativos, middleware, bancos de dados de assinantes, sistemas de análise, sistemas de faturamento ou serviços de conteúdo.

Salvo se expressamente acordado de outra forma em contrato escrito separado, a inext não controla como nossos clientes coletam, usam ou tratam dados de usuários finais por meio de seus próprios serviços de TV, OTT, ISP ou outros.

Os clientes são responsáveis por assegurar que seus próprios serviços, aplicativos, configurações de firmware e atividades de tratamento de dados estejam em conformidade com as leis aplicáveis de privacidade, telecomunicações, proteção ao consumidor e proteção de dados.

## 15. Privacidade de crianças

Nosso site e nossos serviços são destinados a usuários corporativos e não são direcionados a crianças.

Não coletamos conscientemente dados pessoais de crianças por meio do nosso site. Se você acredita que uma criança nos forneceu dados pessoais, entre em contato conosco e tomaremos as medidas adequadas para excluí-los.

## 16. Alterações nesta Política de Privacidade

Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nosso site, em nossas operações comerciais, nos requisitos legais ou nas práticas de tratamento de dados.

A versão atualizada será publicada nesta página com uma nova data de "Última atualização".

## 17. Fale conosco

Para questões sobre esta Política de Privacidade ou sobre nosso tratamento de dados pessoais, entre em contato com:

**INEXT EUROPE s.r.o.**  
**Email:** info@inext.ltd  
**Endereço:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Site:** https://inext.ltd
`;

const spanishPrivacyPolicy = `
**Última actualización:** 2 de junio de 2026

Esta Política de Privacidad explica cómo **inext** ("inext", "nosotros" o "nuestro") recopila, usa, almacena y protege los datos personales cuando usted visita nuestro sitio web **inext.ltd**, se comunica con nosotros, solicita información o interactúa con nosotros en relación con nuestros productos y servicios.

inext desarrolla y suministra set-top boxes Android AOSP, reproductores multimedia y soluciones relacionadas para operadores de TV, proveedores OTT, ISP y otros clientes corporativos.

Esta Política de Privacidad se aplica únicamente a nuestro sitio web y a las comunicaciones corporativas. No se aplica a sitios web, servicios, aplicaciones ni plataformas operadas por clientes de terceros que puedan utilizar productos inext.

## 1. Quiénes somos

El sitio web **inext.ltd** es operado por:

**Empresa:** INEXT EUROPE s.r.o.  
**Domicilio registrado:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Email:** info@inext.ltd  
**Sitio web:** https://inext.ltd

Para consultas relacionadas con la privacidad, puede comunicarse con nosotros en: **info@inext.ltd**.

## 2. Qué datos personales recopilamos

Podemos recopilar datos personales limitados cuando usted interactúa con nuestro sitio web o se comunica con nosotros.

Esto puede incluir:

### Información de contacto y corporativa

Su nombre, nombre de la empresa, cargo, dirección de correo electrónico corporativo, número de teléfono, país y contenido del mensaje.

### Información de la consulta

Interés en productos, requisitos del proyecto, especificaciones solicitadas, cantidades, región, mercado objetivo, necesidades de certificación y otra información que usted decida proporcionar.

### Datos técnicos del sitio web

Dirección IP, tipo de navegador, tipo de dispositivo, sistema operativo, páginas visitadas, sitio web de referencia, ubicación aproximada, fecha y hora de la visita, y datos analíticos similares.

### Datos de comunicación

Correos electrónicos, archivos adjuntos, notas de reuniones y demás correspondencia intercambiada entre usted e inext.

No recopilamos intencionalmente datos personales sensibles a través de nuestro sitio web.

## 3. Cómo recopilamos los datos personales

Recopilamos datos personales cuando usted:

- envía un formulario de contacto;
- solicita una cotización, información sobre productos, documentación técnica o una reunión;
- se comunica con nosotros por correo electrónico, teléfono, mensajería u otros canales corporativos;
- visita o navega por nuestro sitio web;
- interactúa con nuestros materiales de marketing o de desarrollo de negocios.

Algunos datos técnicos pueden recopilarse automáticamente mediante cookies, herramientas de análisis, registros de servidor o tecnologías similares.

## 4. Cómo usamos los datos personales

Usamos los datos personales para fines comerciales legítimos, incluidos los de:

- responder a consultas y solicitudes comerciales;
- proporcionar información sobre nuestros productos, soluciones y capacidades;
- preparar cotizaciones, propuestas y ofertas comerciales;
- evaluar una posible cooperación con socios de TV, OTT, ISP, distribución y tecnología;
- gestionar las relaciones con clientes, proveedores y socios;
- coordinar llamadas, reuniones, demostraciones o conversaciones sobre productos;
- mejorar nuestro sitio web, nuestras comunicaciones y nuestros servicios;
- proteger la seguridad y la integridad de nuestro sitio web y de nuestros sistemas corporativos;
- cumplir con obligaciones legales, fiscales, contables, de control de exportaciones, de sanciones y regulatorias;
- establecer, ejercer o defender reclamaciones legales.

No vendemos datos personales.

## 5. Base legal para el tratamiento

Cuando la legislación de protección de datos aplicable exige una base legal, tratamos los datos personales con fundamento en uno o varios de los siguientes motivos:

### Intereses legítimos

Para comunicaciones corporativas, seguridad del sitio web, marketing B2B, respuesta a consultas y gestión de relaciones comerciales.

### Necesidad precontractual o contractual

Cuando el tratamiento es necesario para preparar o ejecutar un contrato, cotización, pedido, acuerdo de suministro, NDA u otro acuerdo comercial.

### Obligación legal

Cuando debemos tratar o conservar datos para cumplir con leyes aplicables, normas contables, normas fiscales, requisitos aduaneros, verificación de sanciones u otras obligaciones legales.

### Consentimiento

Cuando solicitamos su consentimiento, por ejemplo, para determinadas cookies, boletines o comunicaciones de marketing opcionales.

Usted puede retirar el consentimiento en cualquier momento cuando el tratamiento se base en el consentimiento.

## 6. Comunicaciones de marketing B2B

Podemos contactar a representantes corporativos con información relevante sobre nuestros productos, servicios, actividades comerciales u oportunidades de negocio, cuando lo permita la legislación aplicable.

Usted puede darse de baja de las comunicaciones de marketing en cualquier momento comunicándose con nosotros o utilizando la opción de cancelación de suscripción cuando esté disponible.

Incluso si se da de baja de las comunicaciones de marketing, podremos seguir contactándolo en relación con relaciones comerciales existentes, contratos, pedidos, asuntos técnicos, avisos legales u otras comunicaciones no relacionadas con el marketing.

## 7. Cookies y análisis

Nuestro sitio web puede usar cookies y tecnologías similares para funcionar correctamente, mejorar el rendimiento, comprender el comportamiento de los visitantes y respaldar la seguridad.

Las cookies pueden usarse para:

- funcionalidades esenciales del sitio web;
- análisis y medición del rendimiento;
- recordar preferencias;
- mejorar el contenido y la usabilidad del sitio web.

Cuando la ley lo exija, solicitaremos el consentimiento antes de instalar cookies no esenciales.

Por lo general, usted puede controlar o desactivar las cookies mediante la configuración de su navegador. La desactivación de algunas cookies puede afectar la funcionalidad del sitio web.

## 8. Compartición de datos personales

Podemos compartir datos personales únicamente cuando sea razonablemente necesario para fines comerciales, operativos, legales o de seguridad.

Esto puede incluir la compartición de datos con:

- proveedores de alojamiento y proveedores de servicios de TI;
- proveedores de servicios de correo electrónico, CRM, análisis y comunicación;
- socios de logística, certificación, técnicos y de fabricación cuando sea necesario para un proyecto;
- asesores legales, contables, fiscales, de auditoría y de compliance;
- autoridades públicas, organismos reguladores, tribunales o fuerzas del orden cuando así lo exija la ley;
- posibles compradores, inversores o sucesores en relación con una transacción comercial, fusión, reestructuración o venta de activos.

Exigimos a los proveedores de servicios que traten los datos personales de forma segura y solo para fines autorizados.

## 9. Transferencias internacionales

Como fabricante B2B que trabaja con clientes, socios, proveedores y prestadores de servicios internacionales, podemos tratar o transferir datos personales fuera de su país de residencia.

Cuando lo exija la legislación aplicable, utilizamos salvaguardas adecuadas para las transferencias internacionales de datos, como protecciones contractuales u otros mecanismos legales de transferencia.

## 10. Conservación de datos

Conservamos los datos personales únicamente durante el tiempo razonablemente necesario para los fines descritos en esta Política de Privacidad.

Los períodos de conservación pueden depender de:

- la naturaleza de la relación comercial;
- si estamos discutiendo, preparando o ejecutando un contrato;
- requisitos legales, fiscales, contables, de garantía, de compliance o regulatorios;
- la necesidad de resolver disputas o proteger nuestros derechos legales.

La correspondencia comercial, las cotizaciones y los registros relacionados con contratos pueden conservarse por más tiempo cuando sea necesario para fines legales, contables o comerciales.

## 11. Seguridad de los datos

Utilizamos medidas técnicas y organizativas razonables para proteger los datos personales contra el acceso no autorizado, la pérdida, el uso indebido, la alteración o la divulgación.

Sin embargo, ningún sitio web, sistema de correo electrónico ni método de transmisión en línea es completamente seguro. Usted debe evitar enviar información altamente confidencial a través de canales no seguros, a menos que se acuerden previamente las protecciones adecuadas.

## 12. Sus derechos

Según su ubicación y la legislación aplicable, usted puede tener derecho a:

- acceder a sus datos personales;
- corregir datos inexactos o incompletos;
- solicitar la eliminación de sus datos;
- restringir el tratamiento u oponerse a él;
- retirar el consentimiento cuando el tratamiento se base en el consentimiento;
- solicitar la portabilidad de los datos;
- presentar una reclamación ante una autoridad de protección de datos.

Para ejercer sus derechos, comuníquese con nosotros en **info@inext.ltd**.

Es posible que necesitemos verificar su identidad antes de responder a una solicitud. Algunas solicitudes pueden estar limitadas por obligaciones legales, requisitos contractuales, intereses comerciales legítimos o los derechos de terceros.

## 13. Sitios web y servicios de terceros

Nuestro sitio web puede contener enlaces a sitios web, plataformas, documentación o recursos de socios de terceros.

No somos responsables de las prácticas de privacidad, el contenido ni la seguridad de los sitios web o servicios de terceros. Usted debe revisar sus políticas de privacidad antes de proporcionarles datos personales.

## 14. Datos de productos y plataformas de clientes

inext fabrica y suministra hardware y soluciones relacionadas para clientes corporativos. En muchos casos, nuestros clientes operan sus propias plataformas, servicios, aplicaciones, middleware, bases de datos de suscriptores, sistemas de análisis, sistemas de facturación o servicios de contenido.

Salvo que se acuerde expresamente lo contrario en un acuerdo escrito por separado, inext no controla cómo nuestros clientes recopilan, usan o tratan los datos de los usuarios finales a través de sus propios servicios de TV, OTT, ISP u otros.

Los clientes son responsables de asegurar que sus propios servicios, aplicaciones, configuraciones de firmware y actividades de tratamiento de datos cumplan con las leyes aplicables de privacidad, telecomunicaciones, protección al consumidor y protección de datos.

## 15. Privacidad de los niños

Nuestro sitio web y nuestros servicios están destinados a usuarios corporativos y no están dirigidos a niños.

No recopilamos conscientemente datos personales de niños a través de nuestro sitio web. Si usted cree que un niño nos ha proporcionado datos personales, comuníquese con nosotros y tomaremos las medidas adecuadas para eliminarlos.

## 16. Cambios en esta Política de Privacidad

Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestro sitio web, en nuestras operaciones comerciales, en los requisitos legales o en las prácticas de tratamiento de datos.

La versión actualizada se publicará en esta página con una nueva fecha de "Última actualización".

## 17. Contáctenos

Para consultas sobre esta Política de Privacidad o sobre nuestro tratamiento de datos personales, comuníquese con:

**INEXT EUROPE s.r.o.**  
**Email:** info@inext.ltd  
**Dirección:** Ligurčeková 8, 821 06 Podunajské, Bratislava, Slovakia  
**Sitio web:** https://inext.ltd
`;

// Keyed by locale so a new language compiles immediately (falling back to English
// here) and the search-index generator can import the policies directly. Add an
// entry when its translation lands.
export const privacyPolicies: Partial<Record<Locale, string>> = {
  en: englishPrivacyPolicy,
  uk: ukrainianPrivacyPolicy,
  pl: polishPrivacyPolicy,
  pt: brazilianPrivacyPolicy,
  es: spanishPrivacyPolicy,
};

export function getPrivacyPolicy(locale: Locale): string {
  return privacyPolicies[locale] ?? englishPrivacyPolicy;
}
